'use client';

import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Box, Grid, Typography } from '@mui/material';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type TooltipItem,
} from 'chart.js';

import { StyledTableContainer } from '~/shared';
import type { UnregisteredStatusResponse } from '~/types/monitoring';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface UnregisteredBannersChartProps {
  data: UnregisteredStatusResponse;
}

const getStatusColor = (color: string) => {
  switch (color) {
    case 'VERMELHO':
      return '#EF5350';
    case 'AMARELO':
      return '#FFA726';
    default:
      return '#66BB6A';
  }
};

export const UnregisteredBannersChart = ({
  data,
}: UnregisteredBannersChartProps) => {
  // Filtrar apenas dados de banners
  const bannersData = useMemo(() => {
    return data.filter(item => item.name.includes('(Banners)'));
  }, [data]);

  // Separar impressões e cliques
  const impressionsData = useMemo(() => {
    const foundItem = bannersData.find(item =>
      item.name.includes('Impressões')
    );
    return foundItem || null;
  }, [bannersData]);

  const clicksData = useMemo(() => {
    const foundItem = bannersData.find(item => item.name.includes('Cliques'));
    return foundItem || null;
  }, [bannersData]);

  // Gerar dados do gráfico crescente (simulando crescimento ao longo do tempo)
  // Como a API retorna apenas um valor, vamos criar um array crescente baseado nesse valor
  const generateCumulativeData = (value: number) => {
    // Se o valor for 0, retornar array de zeros
    if (value === 0) {
      return Array(12).fill(0);
    }

    // Gerar 12 meses de dados cumulativos crescente
    const chartData = [];
    const step = value / 12;
    let cumulative = 0;

    for (let i = 0; i < 12; i += 1) {
      cumulative += step;
      chartData.push(Math.round(cumulative));
    }

    return chartData;
  };

  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];

  const impressionsChartData = {
    labels: months,
    datasets: [
      {
        label: 'Impressões (Banners)',
        data: impressionsData
          ? generateCumulativeData(impressionsData.quantity)
          : [],
        borderColor: getStatusColor(impressionsData?.color || 'VERDE'),
        backgroundColor: getStatusColor(impressionsData?.color || 'VERDE'),
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const clicksChartData = {
    labels: months,
    datasets: [
      {
        label: 'Cliques (Banners)',
        data: clicksData ? generateCumulativeData(clicksData.quantity) : [],
        borderColor: getStatusColor(clicksData?.color || 'VERDE'),
        backgroundColor: getStatusColor(clicksData?.color || 'VERDE'),
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  // Calcular o máximo dinâmico baseado nos dados, com mínimo de 300.000
  const maxImpressions = impressionsData?.quantity || 0;
  const maxClicks = clicksData?.quantity || 0;
  const maxValue = Math.max(maxImpressions, maxClicks, 300000);
  // Arredondar para cima para a próxima marca de 50.000 para uma escala mais limpa
  const chartMax = Math.ceil(maxValue / 50000) * 50000;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          title: () => '',
          label: (context: TooltipItem<'line'>) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${(value ?? 0).toLocaleString('pt-BR')}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: chartMax,
        ticks: {
          callback: (value: number | string) => {
            return Number(value).toLocaleString('pt-BR');
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        display: false,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  // Verificar se não há dados ou se ambos têm quantity = 0
  const impressionsQuantity = impressionsData?.quantity ?? 0;
  const clicksQuantity = clicksData?.quantity ?? 0;
  const hasNoBannersData = impressionsQuantity === 0 && clicksQuantity === 0;

  if (hasNoBannersData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="primary.main" sx={{ mb: 1 }}>
          Sem dados de banners não registrados
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Não há impressões ou cliques não registrados de banners no momento.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        {impressionsData && impressionsQuantity > 0 && (
          <Grid item xs={12} md={6}>
            <StyledTableContainer
              sx={{
                p: 2,
                height: 300,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem' }}
              >
                Impressões (Banners)
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Line data={impressionsChartData} options={chartOptions} />
              </Box>
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Status:
                </Typography>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: getStatusColor(impressionsData.color),
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'white', fontWeight: 600 }}
                  >
                    {impressionsData.status}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  Total: {impressionsData.quantity.toLocaleString('pt-BR')}
                </Typography>
              </Box>
            </StyledTableContainer>
          </Grid>
        )}

        {clicksData && clicksQuantity > 0 && (
          <Grid item xs={12} md={6}>
            <StyledTableContainer
              sx={{
                p: 2,
                height: 300,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem' }}
              >
                Cliques (Banners)
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Line data={clicksChartData} options={chartOptions} />
              </Box>
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Status:
                </Typography>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: getStatusColor(clicksData.color),
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'white', fontWeight: 600 }}
                  >
                    {clicksData.status}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  Total: {clicksData.quantity.toLocaleString('pt-BR')}
                </Typography>
              </Box>
            </StyledTableContainer>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
